import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, AlertCircle, Loader2 } from "lucide-react";
import logo from "../assets/dashboard/Avana-logo.svg";
import background from "../assets/dashboard/signinBackground.svg";
import ForgotPassword from "../Components/Layout/ForgotPassword";
import { signInQ } from "../services/authService";

const SignIn = ({ setActiveUser }) => {
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, []);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
    login: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const validateSignIn = (username, password) => {
    let errors = { username: "", password: "", login: "" };

    if (!username) {
      errors.username = "Username is required.";
    }

    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }

    return errors;
  };

  const handleSignIn = async () => {
    setIsLoading(true);
    setLoginSuccess(false);
    setErrors({ username: "", password: "", login: "" });

    const validationErrors = validateSignIn(username, password);
    setErrors(validationErrors);

    if (validationErrors.username || validationErrors.password) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await signInQ(username, password);
      setActiveUser(response);
      setLoginSuccess(true);
      navigate("/dashboard");
    } catch (error) {
      console.error("Login failed", error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        login: error.message || "Login failed. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSignIn();
    }
  };

  const handleForgotPassword = () => {
    setOpenForgotPassword(true);
  };

  return (
    <div className="w-full ">
      <div
        className="fixed inset-0 -top-1/2 sm:-top-1/3 -left-1/4 -z-10 rotate-[-10deg] scale-110 bg-cover bg-center  max-h-[100vh] "
        style={{ backgroundImage: `url(${background})` }}
      />
      <div className="grid justify-center py-10 bg-cover bg-center  ">
        {/* Top Logo */}
        <div className="w-full flex justify-center mb-8">
          <img src={logo} alt="Avana Logo" className="h-24 md:h-36" />
        </div>

        {/* Sign In Card */}
        <div className="bg-white rounded-lg shadow-md p-8 w-[90vw] max-w-md   ">
          <h2 className="text-2xl font-semibold text-center mb-8">Sign in</h2>

          {/* Username Input */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2 text-sm">
              User Name
            </label>
            <input
              type="text"
              placeholder="username"
              className={`w-full p-3 border rounded-md shadow-sm focus:outline-none focus:shadow-lg focus:shadow-gray-200 ${
                errors.username || errors.login
                  ? "border-primary"
                  : "border-gray-300"
              }`}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setErrors((prev) => ({ ...prev, username: "", login: "" }));
              }}
              onKeyDown={handleKeyDown}
            />
            {errors.username && (
              <p className="text-primary text-sm mt-1 flex items-center">
                <AlertCircle size={14} className="mr-1" />
                {errors.username}
              </p>
            )}
          </div>

          {/* Password Input */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700 text-sm">Password</label>
              <button
                onClick={handleForgotPassword}
                className="text-primary text-sm hover:underline font-semibold"
                type="button"
              >
                Forgot Password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="8+ characters required"
                className={`w-full p-3 border rounded-md  shadow-sm focus:outline-none focus:shadow-lg focus:shadow-gray-200 ${
                  errors.password || errors.login
                    ? "border-primary"
                    : "border-gray-300"
                }`}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: "", login: "" }));
                }}
                onKeyDown={handleKeyDown}
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-400"
                onClick={() => setShowPassword(!showPassword)}
              >
                <Eye size={20} />
              </button>
            </div>
            {errors.password && (
              <p className="text-primary text-sm mt-1 flex items-center">
                <AlertCircle size={14} className="mr-1" />
                {errors.password}
              </p>
            )}
          </div>

          {/* Error Message */}
          {errors.login && (
            <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-primary text-sm font-semibold flex items-center">
                <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                {errors.login}
              </p>
            </div>
          )}

          {/* Success Message */}
          {loginSuccess && (
            <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-600 text-sm font-semibold">
                Sign in successful! Redirecting...
              </p>
            </div>
          )}

          {/* Sign In Button */}
          <button
            className={`bg-primary text-white rounded-md p-3 w-full font-medium flex justify-center items-center ${
              isLoading || !username || !password
                ? "opacity-70 cursor-not-allowed"
                : "hover:bg-primary/90"
            }`}
            onClick={handleSignIn}
            disabled={isLoading || !username || !password}
            type="button"
          >
            {isLoading ? (
              <>
                <Loader2 size={20} className="mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </div>
      </div>
      {openForgotPassword && (
        <ForgotPassword handleClose={() => setOpenForgotPassword(false)} />
      )}
    </div>
  );
};

export default SignIn;
